import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import webauthnService from "../services/webauthnService";
import { useAuth } from "../context/AuthContext";
import APP_VERSION from "../version";
import "./Login.css";

const REMEMBER_KEY = "rememberedUsername";

const Login = () => {
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTarget = "/items" + (location.search || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoChecking, setAutoChecking] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      setUsername(saved);
      setRememberMe(true);
    }

    // Auto-trigger passkey/Face ID as soon as the page loads.
    // If no passkey exists on the server, /webauthn/login/begin returns 404
    // and the password form is shown silently.
    (async () => {
      setAutoChecking(true);
      if (!webauthnService.isSupported()) {
        setAutoChecking(false);
        return;
      }
      try {
        const token = await webauthnService.login();
        setToken(token);
        navigate(redirectTarget, { replace: true });
      } catch (err) {
        console.error("[Login] auto passkey login failed:", err);
      } finally {
        setAutoChecking(false);
      }
    })();
  }, [navigate, setToken, redirectTarget]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const token = await authService.login(username, password);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, username);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      setToken(token);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onForgotPassword = (e) => {
    e.preventDefault();
    setError(null);
    setInfo("Password reset is managed by the server administrator.");
  };

  const onLocalLogin = async () => {
    setError(null);
    setInfo(null);
    if (!webauthnService.isSupported()) {
      setError("Your browser or device does not support WebAuthn / Face ID.");
      return;
    }
    setAutoChecking(false);
    setLoading(true);
    try {
      const token = await webauthnService.login();
      setToken(token);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(err.message || "Passkey login failed");
    } finally {
      setLoading(false);
    }
  };

  const cancelAutoCheck = () => {
    setAutoChecking(false);
  };

  return (
    <div className="login-page">
      <div className="login-inner">
        <h1 className="login-title">Welcome back</h1>
        <p className="login-subtitle">Sign in to your Raspberry Pi Server</p>

        {autoChecking ? (
          <div className="login-auto-check">
            <div className="login-spinner" />
            <p>Signing in with Face ID / passkey...</p>
            <button type="button" className="login-cancel-auto" onClick={cancelAutoCheck}>
              Use password
            </button>
          </div>
        ) : (
          <>
        <form onSubmit={onSubmit} className="login-form">
          <div className="login-field">
            <span className="login-field-label">Username</span>
            <div className="login-field-row">
              <svg className="login-field-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
              </svg>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
                required
              />
            </div>
          </div>

          <div className="login-field">
            <span className="login-field-label">Password</span>
            <div className="login-field-row">
              <svg className="login-field-icon" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="login-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 3l18 18" />
                    <path d="M10.6 5.1A11 11 0 0 1 12 5c6.5 0 10 7 10 7a18.4 18.4 0 0 1-2.2 3.2M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7c1.5 0 2.9-.3 4.1-.9" />
                    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="login-options">
            <label className="login-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="login-remember-box" />
              Remember me
            </label>
            <a href="#forgot" className="login-forgot" onClick={onForgotPassword}>
              Forgot password?
            </a>
          </div>

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? "Signing in..." : "Log in"}
          </button>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}
          {info && <div className="login-info">{info}</div>}
        </form>

        <div className="login-divider">
          <span>or</span>
        </div>

        <button type="button" className="login-local-btn" onClick={onLocalLogin}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3Z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          Use local device login
        </button>
          </>
        )}

        <div className="login-illustration" aria-hidden="true">
          <img
            src={process.env.PUBLIC_URL + "/login-house.png"}
            alt="Smart house"
            draggable={false}
          />
        </div>

        <div className="login-version">v{APP_VERSION}</div>
        <div className="login-secure">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3Z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          Secure connection
        </div>
      </div>
    </div>
  );
};

export default Login;
